import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ProductCapabilitiesService, CategoryHierarchyService } from '@core';
import { ENV } from '@di-tokens';
import {
  FAILED_LOADING_PRODUCT_ITEM,
  mapProductDetailsDtoToInternal,
  Paginated,
  ProductDetails,
  ProductDetailsDto,
} from '@models';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SpecialService {
  qty = 20;

  featuredSubject = new BehaviorSubject<Array<ProductDetails>>([]);
  popularSubject = new BehaviorSubject<Array<ProductDetails>>([]);
  recentlyViewedSubject = new BehaviorSubject<Array<ProductDetails>>([]);
  accessorySubject = new BehaviorSubject<Array<ProductDetails>>([]);
  apiUrl = inject(ENV).remoteApi;

  private http = inject(HttpClient);
  private capabilitiesService = inject(ProductCapabilitiesService);
  private hierarchyService = inject(CategoryHierarchyService);

  getFeatured(
    params: {
      pageSize: number;
      brandIds: number[];
    },
    completeCb?: () => void,
  ): void {
    this.http
      .get<Paginated<ProductDetailsDto>>(this.apiUrl + '/products/featured', { params })
      .pipe(
        catchError(() => {
          this.handleProductsError(completeCb);
          return of({ items: [FAILED_LOADING_PRODUCT_ITEM] });
        }),
      )
      .subscribe((retval) => {
        typeof completeCb === 'function' && completeCb();
        if (retval?.items) this.featuredSubject.next(retval?.items.map(mapProductDetailsDtoToInternal));
      });
  }

  getRecentlyViewed(params: { pageSize: number }, completeCb?: () => void): void {
    this.capabilitiesService
      .getAppleCareProductIds()
      .pipe(
        switchMap((appleCareIds) => {
          return this.http
            .get<Paginated<ProductDetailsDto>>(this.apiUrl + '/products/recently-viewed', { params })
            .pipe(
              map((retval) => ({ retval, appleCareIds })),
              catchError(() => {
                this.handleProductsError(completeCb);
                return of({ retval: { items: [FAILED_LOADING_PRODUCT_ITEM] }, appleCareIds });
              }),
            );
        }),
      )
      .subscribe(({ retval, appleCareIds }) => {
        typeof completeCb === 'function' && completeCb();
        if (retval?.items) {
          const filteredDtos = retval.items.filter((dto) => !appleCareIds.includes(dto.productId));
          const mappedProducts = filteredDtos.map(mapProductDetailsDtoToInternal);
          this.recentlyViewedSubject.next(mappedProducts);
        }
      });
  }

  getSimilar(
    // In fact BE expects subcategory ids
    categoryIds: number[],
    count: number,
  ): Observable<ProductDetails[]> {
    return this.capabilitiesService.getAppleCareProductIds().pipe(
      switchMap((appleCareIds) => {
        return this.http
          .get<ProductDetailsDto[]>(this.apiUrl + '/products/similar', { params: { categoryIds, count } })
          .pipe(
            map((response) => {
              // Filter out Apple Care products before mapping
              const filteredResponse = response.filter((dto) => !appleCareIds.includes(dto.productId));
              return filteredResponse.map(mapProductDetailsDtoToInternal);
            }),
            catchError(() => {
              return of([FAILED_LOADING_PRODUCT_ITEM].map(mapProductDetailsDtoToInternal));
            }),
          );
      }),
    );
  }

  getMostPopular(params: { pageSize: number }, completeCb: () => void): void {
    this.http
      .get<Paginated<ProductDetailsDto>>(this.apiUrl + '/products/most-popular', { params })
      .subscribe((retval) => {
        typeof completeCb === 'function' && completeCb();

        if (retval?.items) this.popularSubject.next(retval?.items.map(mapProductDetailsDtoToInternal));
      });
  }

  getYouMayAlsoLikeItems(
    productIds: Array<ProductDetails['productId']>,
    pageSize: number,
  ): Observable<ProductDetails[]> {
    return this.capabilitiesService.getAppleCareProductIds().pipe(
      switchMap((appleCareIds) => {
        return this.http
          .get<ProductDetailsDto[]>(this.apiUrl + '/products/you-may-also-like', {
            params: { productIds, count: pageSize },
          })
          .pipe(
            map((response) => {
              // Filter out Apple Care products before mapping
              const filteredResponse = response.filter((dto) => !appleCareIds.includes(dto.productId));
              return filteredResponse.map(mapProductDetailsDtoToInternal);
            }),
            catchError(() => {
              return of([FAILED_LOADING_PRODUCT_ITEM].map(mapProductDetailsDtoToInternal));
            }),
          );
      }),
    );
  }
  getAccessoryProducts(
    params: {
      categoryId: string | number;
      pageSize?: number;
    },
    completeCb?: () => void,
  ): void {
    // First, find the accessory category using cached hierarchy
    this.hierarchyService
      .findAccessoryCategories(params.categoryId.toString())
      .pipe(
        switchMap(({ firstAccessoryCategoryId }) => {
          if (!firstAccessoryCategoryId) {
            // No accessory category found, return empty result
            typeof completeCb === 'function' && completeCb();
            this.accessorySubject.next([]);
            return of(null);
          }

          // Use the found accessory category ID for the search
          const searchParams = {
            CategoryIds: firstAccessoryCategoryId,
            ...(params.pageSize && { pageSize: params.pageSize }),
          };

          return this.capabilitiesService.getAppleCareProductIds().pipe(
            switchMap((appleCareIds) => {
              return this.http
                .get<Paginated<ProductDetailsDto>>(this.apiUrl + '/search/find', { params: searchParams })
                .pipe(
                  map((retval) => ({ retval, appleCareIds })),
                  catchError(() => {
                    this.handleProductsError(completeCb);
                    return of({ retval: { items: [FAILED_LOADING_PRODUCT_ITEM] }, appleCareIds });
                  }),
                );
            }),
          );
        }),
      )
      .subscribe((result) => {
        if (result) {
          const { retval, appleCareIds } = result;
          typeof completeCb === 'function' && completeCb();

          if (retval?.items) {
            const filteredDtos = retval.items.filter((dto) => !appleCareIds.includes(dto.productId));
            const mappedProducts = filteredDtos.map(mapProductDetailsDtoToInternal);
            this.accessorySubject.next(mappedProducts);
          }
        }
      });
  }

  private handleProductsError(completeCb?: () => void): Observable<Array<unknown>> {
    typeof completeCb === 'function' && completeCb();
    return of([]);
  }
}
